import { useEffect, useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { X, Bot, CheckSquare, ChevronRight, RotateCcw } from "lucide-react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useToast } from "../../components/ui/toast";
import { useCalls } from "./use-calls";
import { CallsToolbar } from "./calls-toolbar";
import {
  CallsRetryBar,
  RETRY_AGENT_ALL,
  RETRY_VERSION_ALL,
  RETRY_OUTCOME_FIELD_ALL,
  RETRY_OUTCOME_VALUE_ANY,
} from "./calls-retry-bar";
import { CallsTable } from "./calls-table";
import { RecordingModal } from "./recording-modal";
import { PcaModal } from "./pca-modal";
import { CallDetailsPanel } from "./call-details-panel";
import { formatTimestamp } from "../../lib/time";
import { formatAvailabilityDate } from "../../lib/availability";
import { DataAvailabilityBanner } from "../analytics/data-availability-banner";
import { usePersistentState } from "../../hooks/use-persistent-state";
import { MultiSelect } from "../../components/ui/multi-select";
import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import {
  CALL_COLUMNS,
  DEFAULT_CALL_COLUMNS,
  DEFAULT_COLUMN_WIDTHS,
  type CallColumnId,
  type ColumnId,
} from "./column-config";
import type { CallRecord } from "./types";
import { type FilterState } from "../../components/ui/filter-popover";
import { useAuth } from "../../context/AuthContext";
import { canViewCustomerPhone } from "../../lib/userAccess";

export function CallsPage() {
  const [params] = useSearchParams();
  // Legacy URL: /call-history?type=chatbot -> redirect to dedicated WhatsApp page
  // so sidebar highlights only WhatsApp > Conversations, not Call History.
  if (params.get("type") === "chatbot") {
    const agent = params.get("agent");
    return <Navigate to={agent ? `/whatsapp/conversations?agent=${encodeURIComponent(agent)}` : "/whatsapp/conversations"} replace />;
  }
  return <VoiceCallsPage historySelector={null} />;
}

function VoiceCallsPage({ historySelector }: { historySelector: React.ReactNode }) {
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({
    ...presetRange("last_7"),
    preset: "last_7",
  }));
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() => ({
    search: searchParams.get("q") || undefined,
    search_field: searchParams.get("q") ? "all" : "contact_id",
    status: undefined,
  }));
  const { calls, dataAvailability, loading, hasMore, loadMore, seedDemoData, queueRetry } = useCalls(dateRange, filters);
  const toast = useToast();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { user } = useAuth();
  // Admin's "Show Customer Phone". Off -> the backend redacts the number anyway,
  // so the column is dropped entirely and Schedule ID takes its place as the
  // second column; on -> Phone is second and Schedule ID third.
  const showPhone = canViewCustomerPhone(user);

  // Key bumped from "call-history:columns": Phone/Schedule used to be one column,
  // so a list persisted under the old key has no "schedule" entry and would hide
  // the new column for everyone who had ever opened this page.
  const [visibleCols, setVisibleCols] = usePersistentState<CallColumnId[]>(
    "call-history:columns:v2", DEFAULT_CALL_COLUMNS,
  );
  const visibleSet = useMemo(() => {
    const next = new Set<CallColumnId>(visibleCols);
    if (!showPhone) next.delete("phone");
    return next;
  }, [visibleCols, showPhone]);

  const availableColumns = useMemo(
    () => CALL_COLUMNS.filter((c) => showPhone || c.id !== "phone"),
    [showPhone],
  );

  const columnOptions = useMemo(() => [
    { value: "contactId", label: "Contact ID", locked: true },
    ...availableColumns.map((c) => ({ value: c.id as string, label: c.label })),
  ], [availableColumns]);

  const [columnWidths, setColumnWidths] = usePersistentState<Record<ColumnId, number>>(
    "call-history:column-widths:v1",
    DEFAULT_COLUMN_WIDTHS,
  );

  const isCustomizedWidths = useMemo(() => {
    return Object.keys(DEFAULT_COLUMN_WIDTHS).some(
      (key) => columnWidths[key as ColumnId] !== DEFAULT_COLUMN_WIDTHS[key as ColumnId],
    );
  }, [columnWidths]);

  const resetAllWidths = () => {
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    toast.info("Column sizes normalized to default");
  };

  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryModalSelection, setRetryModalSelection] = useState<'agent' | 'individual' | null>(null);
  const [retryMode, setRetryMode] = useState(false);
  const [retryAgentFilter, setRetryAgentFilter] = useState(RETRY_AGENT_ALL);
  const [retryVersionFilter, setRetryVersionFilter] = useState(RETRY_VERSION_ALL);
  const [retryOutcomeField, setRetryOutcomeField] = useState(RETRY_OUTCOME_FIELD_ALL);
  const [retryOutcomeValue, setRetryOutcomeValue] = useState(RETRY_OUTCOME_VALUE_ANY);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [recordingCall, setRecordingCall] = useState<CallRecord | null>(null);
  const [pcaCall, setPcaCall] = useState<CallRecord | null>(null);
  const [selectedCall, setSelectedCall] = useState<CallRecord | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const query = searchParams.get("q") || "";
    if (query !== (filters.search || "")) {
      setFilters(prev => ({
        ...prev,
        search: query || undefined,
        search_field: query ? "all" : prev.search_field,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    const params = new URLSearchParams(searchParams);
    if (newFilters.search) {
      params.set("q", newFilters.search);
    } else {
      params.delete("q");
    }
    setSearchParams(params, { replace: true });
  };

  const filtered = useMemo(() => {
    const q = (filters.search || "").trim().toLowerCase();
    const searchField = filters.search_field || "contact_id";
    const status = filters.status || "all";
    const campaign = (filters.campaign || "").trim().toLowerCase();
    const voice = (filters.voice || "").trim().toLowerCase();
    const versionName = (filters.version_name || "").trim().toLowerCase();
    const agentId = filters.agent_id;
    const workflowId = filters.workflow_id;
    const maxDuration = filters.max_duration ? parseFloat(filters.max_duration) : null;
    const durationOp = filters.duration_operator || ">=";

    return calls.filter((c) => {
      if (q) {
        if (searchField === "all") {
          const matches = [
            c.contactId,
            c.phone,
            c.scheduleId,
            c.executionId,
            c.agent,
            c.campaign,
          ].some((value) => String(value || "").toLowerCase().includes(q));
          if (!matches) return false;
        } else {
          const val = searchField === "phone" ? c.phone : (c as any)[
            searchField === "contact_id" ? "contactId" :
            searchField === "schedule_id" ? "scheduleId" :
            searchField === "call_id" ? "executionId" : searchField
          ];
          if (!String(val ?? "").toLowerCase().includes(q)) return false;
        }
      }
      if (status !== "all" && c.status !== status) return false;
      if (campaign && !String(c.campaign || "").toLowerCase().includes(campaign)) return false;
      if (voice && !String(c.voice || "").toLowerCase().includes(voice)) return false;
      if (versionName && !String(c.version || "").toLowerCase().includes(versionName)) return false;
      if (agentId && c.agentId !== agentId) return false;
      if (workflowId && c.workflowId && c.workflowId !== workflowId) return false;
      if (maxDuration !== null) {
        const d = c.durationSec || 0;
        if (durationOp === "<=" && !(d <= maxDuration)) return false;
        if (durationOp === ">=" && !(d >= maxDuration)) return false;
        if (durationOp === "=" && !(d === maxDuration)) return false;
      }
      return true;
    });
  }, [calls, filters]);

  /* Retry-mode derived data: ONLY scheduled calls are eligible for retry */
  const baseRetryEligibleCalls = useMemo(
    () => filtered.filter((c) => c.retryable && c.isScheduledCall),
    [filtered],
  );

  const retryScopeOptions = useMemo(() => {
    const versionsByAgent: Record<string, string[]> = {};
    const allVersions = new Set<string>();
    const agentNamesSet = new Set<string>();

    baseRetryEligibleCalls.forEach((call) => {
      const agentName = String(call?.agent || '').trim();
      const versionName = String(call?.version || '').trim();
      if (!agentName) return;
      agentNamesSet.add(agentName);
      if (!versionsByAgent[agentName]) versionsByAgent[agentName] = [];
      if (versionName) {
        if (!versionsByAgent[agentName].includes(versionName)) {
           versionsByAgent[agentName].push(versionName);
        }
        allVersions.add(versionName);
      }
    });
    
    Object.keys(versionsByAgent).forEach(k => versionsByAgent[k].sort());

    return {
      agentNames: Array.from(agentNamesSet).sort(),
      versionNames: Array.from(allVersions).sort(),
      versionsByAgent,
    };
  }, [baseRetryEligibleCalls]);

  const scopedBaseRetryEligibleCalls = useMemo(() => {
    return baseRetryEligibleCalls.filter(call => {
       const agentMatches = retryAgentFilter === RETRY_AGENT_ALL || call.agent === retryAgentFilter;
       const versionMatches = retryVersionFilter === RETRY_VERSION_ALL || call.version === retryVersionFilter;
       return agentMatches && versionMatches;
    });
  }, [baseRetryEligibleCalls, retryAgentFilter, retryVersionFilter]);

  const retryOutcomeOptions = useMemo(() => {
    if (retryVersionFilter === RETRY_VERSION_ALL) return { fields: [], values: {} };
    const valuesByField: Record<string, Map<string, string>> = {};
    
    scopedBaseRetryEligibleCalls.forEach(call => {
      call.outcomes.forEach(o => {
        if (!o.field || !o.value) return;
        if (!valuesByField[o.field]) valuesByField[o.field] = new Map();
        
        const normalize = (val: string) => {
           const text = val.trim().toLowerCase();
           if (['true', 'yes', 'y', '1'].includes(text)) return 'yes';
           if (['false', 'no', 'n', '0'].includes(text)) return 'no';
           return text;
        };
        const normalized = normalize(o.value);
        if (!valuesByField[o.field].has(normalized)) {
           valuesByField[o.field].set(normalized, o.value);
        }
      });
    });

    const fields = Object.keys(valuesByField).sort();
    const values: Record<string, Array<{ normalized: string; label: string }>> = {};
    fields.forEach(field => {
      values[field] = Array.from(valuesByField[field].entries()).map(([normalized, label]) => ({ normalized, label })).sort((a, b) => a.label.localeCompare(b.label));
    });

    return { fields, values };
  }, [scopedBaseRetryEligibleCalls, retryVersionFilter]);

  const retryEligibleCalls = useMemo(() => {
     return baseRetryEligibleCalls.filter(call => {
       const agentMatches = retryAgentFilter === RETRY_AGENT_ALL || call.agent === retryAgentFilter;
       const versionMatches = retryVersionFilter === RETRY_VERSION_ALL || call.version === retryVersionFilter;
       if (!agentMatches || !versionMatches) return false;

       if (retryOutcomeField === RETRY_OUTCOME_FIELD_ALL) return true;
       const matchedOutcome = call.outcomes.find(o => o.field === retryOutcomeField);
       if (!matchedOutcome) return false;
       if (retryOutcomeValue === RETRY_OUTCOME_VALUE_ANY) return true;
       
       const normalize = (val: string) => {
         const text = val.trim().toLowerCase();
         if (['true', 'yes', 'y', '1'].includes(text)) return 'yes';
         if (['false', 'no', 'n', '0'].includes(text)) return 'no';
         return text;
       };
       return normalize(matchedOutcome.value) === retryOutcomeValue;
     });
  }, [baseRetryEligibleCalls, retryAgentFilter, retryVersionFilter, retryOutcomeField, retryOutcomeValue]);

  // Keep selected items valid when filters change
  useEffect(() => {
    setSelectedIds((currentSelected) => {
      if (currentSelected.size === 0) return currentSelected;
      const allowedIds = new Set(retryEligibleCalls.map((call) => call.executionId));
      const nextSelected = new Set(
        Array.from(currentSelected).filter((id) => allowedIds.has(id))
      );
      if (nextSelected.size === currentSelected.size) return currentSelected;
      return nextSelected;
    });
  }, [retryEligibleCalls]);

  const visible = retryMode ? retryEligibleCalls : filtered;
  const allSelected = visible.length > 0 && visible.every((c) => selectedIds.has(c.executionId));

  /* Schedule alert: search on schedule_id that matches a row */
  const scheduleAlert = useMemo(() => {
    if (filters.search_field !== "schedule_id" || !(filters.search || "").trim()) return null;
    return calls.find((c) => c.scheduleId.toLowerCase() === (filters.search || "").trim().toLowerCase()) ?? null;
  }, [calls, filters.search, filters.search_field]);

  /* Empty-state hint naming the campaign's actual data range. */
  const availabilityNote = useMemo(() => {
    if (!filters.campaign || !dataAvailability) return null;
    const start = formatAvailabilityDate(dataAvailability.campaign_min, (user as any)?.timezone);
    const end = formatAvailabilityDate(dataAvailability.campaign_max, (user as any)?.timezone);
    if (!start || !end) return null;
    const count = dataAvailability.campaign_count ?? 0;
    return `No "${filters.campaign}" calls in this range — ${count} call${count === 1 ? "" : "s"} available from ${start} to ${end}.`;
  }, [filters.campaign, dataAvailability, user]);

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const every = visible.every((c) => next.has(c.executionId));
      visible.forEach((c) => (every ? next.delete(c.executionId) : next.add(c.executionId)));
      return next;
    });

  const retrySelected = async () => {
    const contactIds = Array.from(new Set(
      calls
        .filter((call) => selectedIds.has(call.executionId) && call.retryable && call.isScheduledCall)
        .map((call) => call.contactId)
        .filter(Boolean),
    ));
    if (contactIds.length === 0) {
      toast.warning("No retry contacts found", "Only scheduled calls can be retried.");
      return;
    }

    try {
      setRetrying(true);
      const retry = await queueRetry(contactIds);
      if (!retry.contacts.length) {
        toast.warning("No retry contacts found", "The selected contacts are no longer available in their source batches.");
        return;
      }
      setSelectedIds(new Set());
      setRetryMode(false);
      navigate("/batch/new", {
        state: {
          retryContacts: retry.contacts,
          retryContext: retry.retry_context || null,
          retryLocked: true,
        },
      });
    } catch (error) {
      toast.danger("Could not prepare retry", (error as Error).message);
    } finally {
      setRetrying(false);
    }
  };

  const retryAgentOutcomes = async () => {
    const contactIds = Array.from(new Set(
      retryEligibleCalls
        .map((call) => call.contactId)
        .filter(Boolean),
    ));
    if (contactIds.length === 0) {
      toast.warning("No retry contacts found");
      return;
    }

    try {
      setRetrying(true);
      const retry = await queueRetry(contactIds);
      if (!retry.contacts.length) {
        toast.warning("No retry contacts found", "The selected contacts are no longer available in their source batches.");
        return;
      }
      setShowRetryModal(false);
      setRetryModalSelection(null);
      setRetryMode(false);
      navigate("/batch/new", {
        state: {
          retryContacts: retry.contacts,
          retryContext: retry.retry_context || null,
          retryLocked: true,
        },
      });
    } catch (error) {
      toast.danger("Could not prepare retry", (error as Error).message);
    } finally {
      setRetrying(false);
    }
  };

  const handleExport = () => {
    if (filtered.length === 0) {
      toast.warning("No data to export");
      return;
    }
    
    // Map data based on the currently filtered records. 
    // We export the standard column headers.
    const exportData = filtered.map(c => ({
      "Contact ID": c.contactId || "",
      // Phone and Schedule ID are separate columns here too; the phone is left
      // out entirely for accounts that may not see customer numbers.
      ...(showPhone ? { "Phone": c.phone || "" } : {}),
      "Schedule ID": c.scheduleId || "",
      "Agent": c.agent || "",
      "Version": c.version || "",
      "Voice": c.voice || "",
      "Duration (s)": c.durationSec?.toString() || "0",
      "Timestamp": c.createdAt ? new Date(c.createdAt).toISOString() : "",
      "Status": c.status || "",
      "Hung By": c.hungBy || "",
      "Campaign": c.campaign || "",
      "Sentiment": c.sentiment || "",
      "Execution ID": c.executionId || ""
    }));

    const csvStr = Papa.unparse(exportData);
    const blob = new Blob([csvStr], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    const now = new Date();
    const ts = `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2,'0')}${now.getDate().toString().padStart(2,'0')}_${now.getHours().toString().padStart(2,'0')}${now.getMinutes().toString().padStart(2,'0')}`;
    const filename = `call_history_export_${ts}.csv`;
    
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${filtered.length} calls`);
  };

  return (
    <div className="space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4">
      {historySelector}
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-sm font-semibold text-ink">Call History</h1>
          
        </div>
        {retryMode && selectedIds.size > 0 && (
          <button
            onClick={retrySelected}
            disabled={retrying}
            className="rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover disabled:cursor-wait disabled:opacity-60"
          >
            {retrying ? "Preparing..." : `Retry Selected (${selectedIds.size})`}
          </button>
        )}
      </div>

      {scheduleAlert && (
        <div className="rounded-lg border border-brand/20 bg-brand-soft px-4 py-2.5 text-sm font-medium text-brand">
          Schedule matched: {scheduleAlert.scheduleId} · dialing {formatTimestamp(scheduleAlert.createdAt, (user as any)?.timezone)}
        </div>
      )}

      {/* Campaign data range: when a campaign is selected (e.g. Last 7 Days +
          a campaign with older data), show where that campaign's calls live. */}
      {!loading && (filters.campaign || filtered.length === 0) && dataAvailability && (
        <DataAvailabilityBanner
          availability={dataAvailability}
          campaign={filters.campaign}
          totalCalls={filtered.length}
          onJumpToData={(start, end) => setDateRange({ start, end, preset: null })}
        />
      )}

      <CallsToolbar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        filters={filters}
        onFiltersChange={updateFilters}
        retryMode={retryMode || showRetryModal} 
        onRetryMode={(v) => { 
          if (v) {
            setShowRetryModal(true);
            setRetryModalSelection(null);
          } else {
            setShowRetryModal(false);
            setRetryMode(false);
            setRetryModalSelection(null);
            setSelectedIds(new Set());
            setRetryAgentFilter(RETRY_AGENT_ALL);
            setRetryVersionFilter(RETRY_VERSION_ALL);
            setRetryOutcomeField(RETRY_OUTCOME_FIELD_ALL);
            setRetryOutcomeValue(RETRY_OUTCOME_VALUE_ANY);
          }
        }}
        onScrollLeft={() => scrollRef.current?.scrollBy({ left: -320, behavior: "smooth" })}
        onScrollRight={() => scrollRef.current?.scrollBy({ left: 320, behavior: "smooth" })}
        onRefresh={() => toast.info("Refreshing call history…")}
        onExport={handleExport}
        rightSlot={
          <div className="flex items-center gap-2">
            {isCustomizedWidths && (
              <button
                onClick={resetAllWidths}
                title="Normalize all column sizes to default"
                className="flex items-center gap-1.5 rounded-lg border border-line bg-soft px-2.5 py-2 text-xs font-medium text-ink-2 transition-colors hover:bg-line/60 hover:text-ink"
              >
                <RotateCcw className="h-3.5 w-3.5 text-ink-3" />
                <span className="hidden sm:inline">Normalize Sizes</span>
              </button>
            )}
            <MultiSelect
              buttonLabel="Columns"
              options={columnOptions}
              selected={visibleSet}
              defaultSelected={["contactId", ...availableColumns.map((c) => c.id)]}
              onChange={(next) =>
                setVisibleCols(
                  CALL_COLUMNS.filter((c) =>
                    // Phone isn't offered while the account can't see numbers, so
                    // `next` never carries it -- keep whatever was stored instead
                    // of dropping it, or enabling the admin toggle later would
                    // leave the column hidden with no way to bring it back.
                    c.id === "phone" && !showPhone ? visibleCols.includes("phone") : next.has(c.id),
                  ).map((c) => c.id), // canonical order
                )
              }
            />
          </div>
        }
      />

      {showRetryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl relative">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-ink">Retry Mode</h2>
                
              </div>
              <button onClick={() => {
                setShowRetryModal(false);
                setRetryMode(false);
              }} className="p-1 hover:bg-soft rounded-lg transition-colors absolute top-6 right-6">
                <X className="h-5 w-5 text-ink-2" />
              </button>
            </div>

            {retryModalSelection === null ? (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setRetryModalSelection('agent')}
                  className="group flex flex-col items-start text-left p-6 rounded-xl border border-line hover:border-brand bg-white transition-all shadow-sm hover:shadow-md"
                >
                  <div className="mb-4 rounded-lg bg-soft p-2.5 text-ink group-hover:text-brand group-hover:bg-brand-soft transition-colors">
                    <Bot className="h-6 w-6" />
                  </div>
                  <div className="font-semibold text-ink text-base">Retry by outcomes</div>
                  <div className="text-sm text-ink-2 mt-2 leading-relaxed flex-1">
                    Select an agent, version, and specific outcomes to dynamically retry scheduled batch calls.
                  </div>
                  <div className="mt-6 flex items-center text-sm font-semibold text-brand">
                    Select criteria <ChevronRight className="ml-1 h-4 w-4" />
                  </div>
                </button>
                <button
                  onClick={() => {
                    setRetryModalSelection('individual');
                    setShowRetryModal(false);
                    setRetryMode(true);
                  }}
                  className="group flex flex-col items-start text-left p-6 rounded-xl border border-line hover:border-brand bg-white transition-all shadow-sm hover:shadow-md"
                >
                  <div className="mb-4 rounded-lg bg-soft p-2.5 text-ink group-hover:text-brand group-hover:bg-brand-soft transition-colors">
                    <CheckSquare className="h-6 w-6" />
                  </div>
                  <div className="font-semibold text-ink text-base">Individual selection</div>
                  <div className="text-sm text-ink-2 mt-2 leading-relaxed flex-1">
                    Manually select specific contacts from scheduled calls using checkboxes to retry.
                  </div>
                  <div className="mt-6 flex items-center text-sm font-semibold text-ink group-hover:text-brand transition-colors">
                    Select manually <ChevronRight className="ml-1 h-4 w-4" />
                  </div>
                </button>
              </div>
            ) : retryModalSelection === 'agent' ? (
              <div className="space-y-6 mt-4">
                <CallsRetryBar
                  retryAgentFilter={retryAgentFilter}
                  setRetryAgentFilter={setRetryAgentFilter}
                  retryVersionFilter={retryVersionFilter}
                  setRetryVersionFilter={setRetryVersionFilter}
                  retryOutcomeField={retryOutcomeField}
                  setRetryOutcomeField={setRetryOutcomeField}
                  retryOutcomeValue={retryOutcomeValue}
                  setRetryOutcomeValue={setRetryOutcomeValue}
                  retryScopeOptions={retryScopeOptions}
                  retryOutcomeOptions={retryOutcomeOptions}
                  matchCount={retryEligibleCalls.length}
                  totalCount={scopedBaseRetryEligibleCalls.length}
                  onClear={() => {
                    setRetryAgentFilter(RETRY_AGENT_ALL);
                    setRetryVersionFilter(RETRY_VERSION_ALL);
                    setRetryOutcomeField(RETRY_OUTCOME_FIELD_ALL);
                    setRetryOutcomeValue(RETRY_OUTCOME_VALUE_ANY);
                  }}
                />
                
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => {
                      setRetryModalSelection(null);
                    }}
                    className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft transition-colors"
                  >
                    Back
                  </button>
                  <button
                    disabled={retryEligibleCalls.length === 0}
                    onClick={() => {
                      setSelectedIds(new Set(retryEligibleCalls.map(c => c.executionId)));
                      setShowRetryModal(false);
                      setRetryMode(true);
                    }}
                    className="rounded-lg border border-brand text-brand px-6 py-2 text-sm font-semibold hover:bg-brand-soft disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Show Contacts
                  </button>
                  <button
                    disabled={retrying || retryEligibleCalls.length === 0}
                    onClick={retryAgentOutcomes}
                    className="rounded-lg bg-brand px-6 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {retrying ? "Preparing..." : `Retry Selected (${retryEligibleCalls.length})`}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <CallsTable
        scrollRef={scrollRef}
        visible={visibleSet}
        calls={visible}
        retryMode={retryMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        allSelected={allSelected}
        onOpenRecording={setRecordingCall}
        onOpenPca={setPcaCall}
        onRowClick={setSelectedCall}
        isFiltered={Object.keys(filters).length > 0}
        hasMore={hasMore}
        loading={loading}
        availabilityNote={availabilityNote}
        onLoadMore={loadMore}
        onSeed={() => { seedDemoData(); toast.info("Seeding demo data…"); }}
        columnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
      />

      {recordingCall && <RecordingModal call={recordingCall} onClose={() => setRecordingCall(null)} />}
      {pcaCall && <PcaModal call={pcaCall} onClose={() => setPcaCall(null)} />}
      {selectedCall && <CallDetailsPanel call={selectedCall} onClose={() => setSelectedCall(null)} />}
    </div>
  );
}
